import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail, downloadFile } from "@/lib/api";
import { formatDate } from "@/lib/labels";
import PgaMonitoringFlow from "@/components/PgaMonitoringFlow";
import RecordActions from "@/components/RecordActions";
import ConfirmDeleteDialog from "@/components/ConfirmDeleteDialog";
import ReviewDialog from "@/components/ReviewDialog";
import MonitoringEditDialog from "@/components/MonitoringEditDialog";
import { StructureHistoryDialog, PgaAiAnalysisDialog, PgaMonitoringDetailDialog, TrendIcon, PGA_LEVEL_LABELS, PGA_LEVEL_COLORS, PGA_LEVEL_TEXT, PGA_LEVEL_BORDER } from "@/components/PgaExtras";
import PgaHistoryPanel from "@/components/PgaHistoryPanel";
import PgaChecklistConfigDialog from "@/components/PgaChecklistConfigDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { Wheat, Plus, Pencil, Trash2, Power, PlayCircle, History, Gauge, Bug, Sparkles, ClipboardList, LayoutGrid, FileDown } from "lucide-react";

const INSPECT = ["admin", "supervisor", "tecnico", "gestor", "coordenador", "responsavel_tecnico"];
const tooltipStyle = { backgroundColor: "hsl(240,10%,10%)", border: "1px solid hsl(240,10%,20%)", borderRadius: 8, fontSize: 12, color: "#fff" };

function StructureDialog({ open, onOpenChange, initial, clients, types, onSaved }) {
  const empty = { name: "", code: "", client_id: "", unit_id: "", type_id: "", capacity: "", description: "", lat: "", lng: "" };
  const [form, setForm] = useState(empty);
  const [units, setUnits] = useState([]);

  useEffect(() => {
    if (open) setForm(initial ? {
      name: initial.name, code: initial.code || "", client_id: initial.client_id, unit_id: initial.unit_id,
      type_id: initial.type_id, capacity: initial.capacity || "", description: initial.description || "",
      lat: initial.geo?.lat ?? "", lng: initial.geo?.lng ?? "",
    } : empty);
  }, [open, initial]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (form.client_id) api.get("/units", { params: { client_id: form.client_id } }).then(({ data }) => setUnits(data));
    else setUnits([]);
  }, [form.client_id]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    if (form.name.trim().length < 2) return toast.error("Informe o nome da estrutura");
    if (!form.client_id || !form.unit_id) return toast.error("Selecione cliente e unidade");
    if (!form.type_id) return toast.error("Selecione o tipo de estrutura");
    const body = {
      ...form,
      lat: form.lat === "" ? null : parseFloat(form.lat),
      lng: form.lng === "" ? null : parseFloat(form.lng),
    };
    try {
      if (initial?.id) await api.put(`/pga/structures/${initial.id}`, body);
      else await api.post("/pga/structures", body);
      toast.success(initial?.id ? "Estrutura atualizada" : "Estrutura cadastrada");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="pga-structure-dialog">
        <DialogHeader><DialogTitle className="font-heading">{initial?.id ? "Editar Estrutura" : "Nova Estrutura de Armazenagem"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <Input className="col-span-2" value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="Nome (ex.: Silo 01) *" data-testid="pga-structure-name-input" />
            <Input value={form.code} onChange={(e) => set({ code: e.target.value })} placeholder="Código" data-testid="pga-structure-code-input" />
          </div>
          <Select value={form.type_id} onValueChange={(v) => set({ type_id: v })}>
            <SelectTrigger data-testid="pga-structure-type-select"><SelectValue placeholder="Tipo de estrutura *" /></SelectTrigger>
            <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={form.client_id} onValueChange={(v) => set({ client_id: v, unit_id: "" })}>
            <SelectTrigger data-testid="pga-structure-client-select"><SelectValue placeholder="Cliente *" /></SelectTrigger>
            <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={form.unit_id} onValueChange={(v) => set({ unit_id: v })} disabled={!form.client_id}>
            <SelectTrigger data-testid="pga-structure-unit-select"><SelectValue placeholder="Unidade *" /></SelectTrigger>
            <SelectContent>{units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
          <div className="grid grid-cols-3 gap-3">
            <Input value={form.capacity} onChange={(e) => set({ capacity: e.target.value })} placeholder="Capacidade (ex.: 5.000 t)" data-testid="pga-structure-capacity-input" />
            <Input type="number" step="any" value={form.lat} onChange={(e) => set({ lat: e.target.value })} placeholder="Latitude" data-testid="pga-structure-lat-input" />
            <Input type="number" step="any" value={form.lng} onChange={(e) => set({ lng: e.target.value })} placeholder="Longitude" data-testid="pga-structure-lng-input" />
          </div>
          <Textarea rows={2} value={form.description} onChange={(e) => set({ description: e.target.value })} placeholder="Descrição / localização física" className="resize-none" data-testid="pga-structure-description-input" />
          <Button className="w-full" onClick={save} data-testid="pga-structure-save-btn">Salvar Estrutura</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function GrainPests() {
  const { user, can, hasConfig } = useAuth();
  // Mesmas regras das rotas /pga: estruturas = pga_estruturas.<ação>; checklist
  // dos tipos = catálogo GLOBAL (grupo de configuração operacional).
  const canCreate = can("pga_estruturas", "create");
  const canEdit = can("pga_estruturas", "edit");
  const canDelete = can("pga_estruturas", "delete");
  const canManage = canEdit || canDelete;
  const canConfigureChecklist = hasConfig("operacional") && canEdit;
  const canInspect = INSPECT.includes(user.role);
  const [clients, setClients] = useState([]);
  const [units, setUnits] = useState([]);
  const [clientId, setClientId] = useState("todos");
  const [unitId, setUnitId] = useState("todas");
  const [dash, setDash] = useState(null);
  const [types, setTypes] = useState([]);
  const [allStructures, setAllStructures] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [flow, setFlow] = useState(null);
  const [historyId, setHistoryId] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [monEdit, setMonEdit] = useState(null);
  const [monReview, setMonReview] = useState(null);
  const [monDelete, setMonDelete] = useState(null);

  const params = () => {
    const p = {};
    if (clientId !== "todos") p.client_id = clientId;
    if (unitId !== "todas") p.unit_id = unitId;
    return p;
  };

  const loadDash = () => api.get("/pga/dashboard", { params: params() }).then(({ data }) => setDash(data))
    .catch((err) => { if (err.response?.status === 403) setForbidden(true); });
  const loadStructures = () => api.get("/pga/structures", { params: { ...params(), include_inactive: true } }).then(({ data }) => setAllStructures(data)).catch(() => {});
  // O histórico é carregado pelo PgaHistoryPanel (com filtros e paginação).
  // NÃO carregamos mais a lista aqui: o endpoint devolvia 500 e o erro era
  // engolido em silêncio, fazendo o histórico "sumir".
  const loadAll = () => { loadDash(); loadStructures(); };

  useEffect(() => {
    if (user.role !== "cliente") api.get("/clients").then(({ data }) => setClients(data)).catch(() => {});
    api.get("/pga/structure-types").then(({ data }) => setTypes(data)).catch(() => {});
  }, [user.role]);

  useEffect(() => {
    if (clientId !== "todos") api.get("/units", { params: { client_id: clientId } }).then(({ data }) => setUnits(data));
    else setUnits([]);
    setUnitId("todas");
  }, [clientId]);

  useEffect(() => { loadAll(); }, [clientId, unitId]); // eslint-disable-line react-hooks/exhaustive-deps

  const startMonitoring = (startId = null) => {
    const active = (dash?.structures || []).filter((s) => s.active !== false);
    if (!active.length) return toast.error("Nenhuma estrutura ativa para monitorar nos filtros atuais");
    const startIndex = startId ? Math.max(0, active.findIndex((s) => s.id === startId)) : 0;
    setFlow({ structures: active, startIndex });
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const q = new URLSearchParams();
      if (clientId !== "todos") q.set("client_id", clientId);
      if (unitId !== "todas") q.set("unit_id", unitId);
      await downloadFile(`/reports/pga/pdf?${q.toString()}`, "relatorio-pga.pdf");
      toast.success("Relatório executivo PGA gerado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha ao gerar o relatório");
    } finally {
      setPdfLoading(false);
    }
  };

  const toggleStructure = async (s) => {
    try {
      await api.patch(`/pga/structures/${s.id}/toggle`);
      toast.success(s.active === false ? "Estrutura ativada" : "Estrutura desativada (histórico preservado)");
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteStructure = async (s) => {
    if (!window.confirm(`Excluir a estrutura "${s.name}"?`)) return;
    try {
      await api.delete(`/pga/structures/${s.id}`);
      toast.success("Estrutura excluída");
      loadAll();
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      if (String(detail).includes("cascata") && window.confirm(`${detail}\n\nConfirma excluir a estrutura E todos os monitoramentos dela?`)) {
        try {
          await api.delete(`/pga/structures/${s.id}?cascade=true`);
          toast.success("Estrutura e monitoramentos excluídos");
          loadAll();
          return;
        } catch (e2) {
          return toast.error(formatApiErrorDetail(e2.response?.data?.detail));
        }
      }
      toast.error(detail);
    }
  };

  if (flow) {
    return (
      <PgaMonitoringFlow structures={flow.structures} types={types} startIndex={flow.startIndex}
        onExit={() => { setFlow(null); loadAll(); }}
        onFinished={() => { setFlow(null); loadAll(); }} />
    );
  }

  if (forbidden) {
    return (
      <Card className="max-w-xl mx-auto p-10 border-border bg-card text-center mt-10" data-testid="pga-forbidden">
        <Wheat size={34} className="mx-auto text-muted-foreground" />
        <p className="text-sm text-muted-foreground mt-3">O módulo Pragas em Grãos Armazenados não está habilitado para o seu cliente. Fale com a administração da ASTRAL.</p>
      </Card>
    );
  }

  if (!dash) {
    return <div className="flex items-center justify-center h-64"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;
  }

  const counts = dash.counts || {};
  const cap = dash.capturas || {};

  return (
    <div className="space-y-6" data-testid="pga-page">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Wheat size={26} className="text-yellow-500" /> Pragas em Grãos Armazenados
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Monitoramento de silos, armazéns e estruturas com índice de infestação, capturas por espécie e análise IA especializada.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {user.role !== "cliente" && (
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="w-44 h-9" data-testid="pga-client-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os clientes</SelectItem>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={unitId} onValueChange={setUnitId} disabled={clientId === "todos"}>
            <SelectTrigger className="w-44 h-9" data-testid="pga-unit-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas as unidades</SelectItem>
              {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {user.features?.relatorios && (
            <Button variant="outline" onClick={downloadPdf} disabled={pdfLoading} data-testid="pga-pdf-btn">
              <FileDown size={15} className="mr-2" /> {pdfLoading ? "Gerando..." : "Relatório PDF"}
            </Button>
          )}
          {canConfigureChecklist && (
            <Button variant="outline" onClick={() => setChecklistOpen(true)} data-testid="pga-checklist-config-btn">
              <ClipboardList size={15} className="mr-2" /> Configurar checklist
            </Button>
          )}
          {canInspect && (
            <Button variant="outline" onClick={() => setAiOpen(true)} data-testid="pga-ai-btn">
              <Sparkles size={15} className="mr-2 text-primary" /> Análise IA
            </Button>
          )}
          {canInspect && (
            <Button onClick={() => startMonitoring()} data-testid="pga-start-monitoring-btn">
              <PlayCircle size={15} className="mr-2" /> Iniciar Monitoramento
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        <Card className="p-3 border-border bg-card xl:col-span-2" data-testid="pga-kpi-media">
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Índice médio de infestação</p>
            <Gauge size={14} className="text-yellow-500" />
          </div>
          <p className="font-heading text-3xl font-bold mt-1">{dash.media_indice}<span className="text-sm text-muted-foreground font-normal">/100</span></p>
        </Card>
        {["baixo", "medio", "alto", "critico"].map((k) => (
          <Card key={k} className={`p-3 border bg-card ${PGA_LEVEL_BORDER[k]}`} data-testid={`pga-count-${k}`}>
            <p className={`font-heading text-2xl font-bold ${PGA_LEVEL_TEXT[k]}`}>{counts[k] || 0}</p>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{PGA_LEVEL_LABELS[k]}</p>
          </Card>
        ))}
        <Card className="p-3 border-border bg-card" data-testid="pga-kpi-capturas">
          <div className="flex items-center justify-between">
            <p className="font-heading text-2xl font-bold text-yellow-500">{cap.total || 0}</p>
            <TrendIcon trend={cap.tendencia} size={15} />
          </div>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">Pragas capturadas</p>
        </Card>
        <Card className="p-3 border-border bg-card" data-testid="pga-kpi-monitoradas">
          <p className="font-heading text-2xl font-bold">{dash.monitoradas_30d || 0}<span className="text-sm text-muted-foreground font-normal">/{dash.total || 0}</span></p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">Monitoradas (30d)</p>
        </Card>
      </div>

      <Tabs defaultValue="painel">
        <TabsList data-testid="pga-tabs">
          <TabsTrigger value="painel" data-testid="pga-tab-painel"><LayoutGrid size={14} className="mr-1.5" /> Painel</TabsTrigger>
          <TabsTrigger value="estruturas" data-testid="pga-tab-estruturas"><Wheat size={14} className="mr-1.5" /> Estruturas</TabsTrigger>
          <TabsTrigger value="monitoramentos" data-testid="pga-tab-monitoramentos"><History size={14} className="mr-1.5" /> Monitoramentos</TabsTrigger>
        </TabsList>

        <TabsContent value="painel" className="mt-4 space-y-5">
          {dash.structures.length === 0 ? (
            <Card className="p-10 border-border bg-card text-center">
              <Wheat size={32} className="mx-auto text-muted-foreground" />
              <p className="text-sm text-muted-foreground mt-3">Nenhuma estrutura de armazenagem cadastrada nos filtros atuais.</p>
              {canCreate && (
                <Button variant="outline" className="mt-4" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="pga-empty-add-btn">
                  <Plus size={15} className="mr-2" /> Cadastrar primeira estrutura
                </Button>
              )}
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {dash.structures.map((s) => {
                const inf = s.infestation || { index: 0, level: "baixo", trend: "estavel" };
                return (
                  <Card key={s.id} className={`p-4 border-2 bg-card ${PGA_LEVEL_BORDER[inf.level]}`} data-testid={`pga-structure-card-${s.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-heading font-semibold text-sm truncate">{s.name}{s.code ? ` · ${s.code}` : ""}</p>
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{s.type_name} · {s.client_name} › {s.unit_name}</p>
                      </div>
                      <Badge variant="outline" className={PGA_LEVEL_COLORS[inf.level]} data-testid={`pga-structure-level-${s.id}`}>{PGA_LEVEL_LABELS[inf.level]}</Badge>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-3">
                      <div className="text-xs text-muted-foreground space-y-0.5">
                        {s.last_monitoring_at ? <p>Último monitoramento: {formatDate(s.last_monitoring_at)}</p> : <p>Nunca monitorada</p>}
                        {s.capacity && <p>Capacidade: {s.capacity}</p>}
                      </div>
                      <div className="text-center shrink-0" title="Índice de infestação (0-100)" data-testid={`pga-structure-index-${s.id}`}>
                        <p className={`font-heading text-xl font-bold leading-none flex items-center gap-1 ${PGA_LEVEL_TEXT[inf.level]}`}>{inf.index}<TrendIcon trend={inf.trend} size={11} /></p>
                        <p className="text-[8px] uppercase tracking-wider text-muted-foreground flex items-center gap-0.5 justify-center mt-0.5"><Bug size={9} /> Infestação</p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      {canInspect && (
                        <Button variant="outline" size="sm" className="flex-1" onClick={() => startMonitoring(s.id)} data-testid={`pga-monitor-btn-${s.id}`}>
                          <PlayCircle size={13} className="mr-1.5" /> Monitorar
                        </Button>
                      )}
                      <Button variant="outline" size="sm" className="flex-1" onClick={() => setHistoryId(s.id)} data-testid={`pga-history-btn-${s.id}`}>
                        <History size={13} className="mr-1.5" /> Histórico
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="p-5 border-border bg-card" data-testid="pga-species-chart">
              <h3 className="font-heading font-semibold text-sm mb-3">Capturas por espécie</h3>
              {!cap.por_especie?.length ? <p className="text-sm text-muted-foreground py-6 text-center">Nenhuma captura registrada.</p> : (
                <div className="space-y-2">
                  {cap.por_especie.slice(0, 8).map((e, i) => (
                    <div key={`${e.name}-${i}`} className="flex items-center gap-2">
                      <p className="text-xs flex-1 truncate">{e.name}</p>
                      <div className="h-1.5 w-28 rounded-full bg-muted shrink-0">
                        <div className="h-full rounded-full bg-yellow-500" style={{ width: `${(e.total / (cap.por_especie[0]?.total || 1)) * 100}%` }} />
                      </div>
                      <span className="text-xs font-semibold w-8 text-right">{e.total}</span>
                      <span className="text-[10px] text-muted-foreground w-10 text-right">{e.pct}%</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
            <Card className="p-5 border-border bg-card" data-testid="pga-evolution-chart">
              <h3 className="font-heading font-semibold text-sm mb-3">Evolução mensal de capturas</h3>
              {!dash.evolucao_mensal?.length ? <p className="text-sm text-muted-foreground py-6 text-center">Sem dados no período.</p> : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={dash.evolucao_mensal}>
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: "hsl(240,5%,60%)" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: "hsl(240,5%,60%)" }} axisLine={false} tickLine={false} width={28} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="total" fill="#eab308" radius={[4, 4, 0, 0]} barSize={22} name="Capturas" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>

          {dash.criticos?.length > 0 && (
            <Card className="p-5 border-border bg-card" data-testid="pga-criticos-card">
              <h3 className="font-heading font-semibold text-sm mb-3">Estruturas em atenção (índice alto/crítico)</h3>
              <div className="space-y-2">
                {dash.criticos.map((c, i) => (
                  <div key={c.id} className="flex items-center justify-between gap-3 text-sm border-b border-border/50 last:border-0 pb-2 last:pb-0">
                    <p className="truncate"><span className="font-medium">{c.name}</span> <span className="text-xs text-muted-foreground">({c.type_name})</span></p>
                    <Badge variant="outline" className={PGA_LEVEL_COLORS[c.infestation.level]}>{c.infestation.index}/100 · {PGA_LEVEL_LABELS[c.infestation.level]}</Badge>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="estruturas" className="mt-4">
          <Card className="border-border bg-card">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <p className="font-heading font-semibold text-sm">Cadastro de Estruturas ({allStructures.length})</p>
              {canCreate && (
                <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="pga-add-structure-btn">
                  <Plus size={14} className="mr-1.5" /> Nova Estrutura
                </Button>
              )}
            </div>
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b border-border">
                    <th className="p-3 font-medium">Estrutura</th>
                    <th className="p-3 font-medium">Tipo</th>
                    <th className="p-3 font-medium">Cliente / Unidade</th>
                    <th className="p-3 font-medium">Infestação</th>
                    <th className="p-3 font-medium">Status</th>
                    {canManage && <th className="p-3 font-medium text-right">Ações</th>}
                  </tr>
                </thead>
                <tbody>
                  {allStructures.map((s) => (
                    <tr key={s.id} className="border-b border-border/50 last:border-0" data-testid={`pga-structure-row-${s.id}`}>
                      <td className="p-3">
                        <p className="font-medium">{s.name}{s.code ? ` · ${s.code}` : ""}</p>
                        {s.capacity && <p className="text-xs text-muted-foreground">Capacidade: {s.capacity}</p>}
                      </td>
                      <td className="p-3 text-muted-foreground">{s.type_name}</td>
                      <td className="p-3 text-muted-foreground">{s.client_name} › {s.unit_name}</td>
                      <td className="p-3">
                        <Badge variant="outline" className={PGA_LEVEL_COLORS[s.infestation_level || "baixo"]}>
                          {s.infestation_index ?? 0}/100 · {PGA_LEVEL_LABELS[s.infestation_level || "baixo"]}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <Badge variant="outline" className={s.active === false ? "bg-slate-500/15 text-slate-400 border-slate-500/30" : "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"}>
                          {s.active === false ? "Inativa" : "Ativa"}
                        </Badge>
                      </td>
                      {canManage && (
                        <td className="p-3">
                          <div className="flex items-center justify-end gap-1">
                            {canEdit && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditing(s); setDialogOpen(true); }} data-testid={`pga-edit-btn-${s.id}`}><Pencil size={14} /></Button>}
                            {canEdit && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => toggleStructure(s)} data-testid={`pga-toggle-btn-${s.id}`} title={s.active === false ? "Ativar" : "Desativar"}><Power size={14} /></Button>}
                            {canDelete && (
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteStructure(s)} data-testid={`pga-delete-btn-${s.id}`}><Trash2 size={14} /></Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                  {allStructures.length === 0 && (
                    <tr><td colSpan={6} className="p-6 text-center text-muted-foreground text-sm">Nenhuma estrutura cadastrada.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="monitoramentos" className="mt-4">
          <PgaHistoryPanel
            clientId={clientId}
            unitId={unitId}
            structures={allStructures}
            onOpenDetail={(id) => setDetailId(id)}
          />
        </TabsContent>
      </Tabs>

      <StructureDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={editing} clients={clients} types={types} onSaved={loadAll} />
      <StructureHistoryDialog structureId={historyId} onClose={() => setHistoryId(null)} />
      <PgaMonitoringDetailDialog monitoringId={detailId} onClose={() => setDetailId(null)} />
      <PgaAiAnalysisDialog open={aiOpen} onOpenChange={setAiOpen} clientId={clientId} unitId={unitId} />
      <PgaChecklistConfigDialog
        open={checklistOpen}
        onOpenChange={setChecklistOpen}
        structureTypes={types}
        onSaved={loadAll}
      />
      <MonitoringEditDialog open={!!monEdit} onOpenChange={(v) => !v && setMonEdit(null)} kind="pga"
        record={monEdit} onSaved={loadAll} />
      <ReviewDialog open={!!monReview} onOpenChange={(v) => !v && setMonReview(null)} entity="monitoramento"
        entityLabel={monReview?.structure_name} url={`/pga/monitorings/${monReview?.id}/review`} onDone={loadAll} />
      <ConfirmDeleteDialog open={!!monDelete} onOpenChange={(v) => !v && setMonDelete(null)} entity="monitoramento"
        entityLabel={`${monDelete?.structure_name || ""} — ${formatDate(monDelete?.finished_at)}`}
        impactUrl={monDelete ? `/pga/monitorings/${monDelete.id}/delete-impact` : null}
        deleteUrl={`/pga/monitorings/${monDelete?.id}`}
        cascadeLabel="Excluir também as ocorrências geradas por este monitoramento"
        onDeleted={loadAll} />
    </div>
  );
}
